import { useRef, useEffect } from 'react';
import type { RoleplayStatus } from '../types';

/**
 * スペースキーで発話開始・終了するイベントリスナーと speakButtonRef を管理するフック。
 * toggleRecording はページ側で定義し、このフックに渡す。
 */
export interface UseRoleplayRecordingParams {
  toggleRecording: () => Promise<void>;
  status: RoleplayStatus;
  openConfirmDialog: boolean;
  showMicDialog: boolean;
}

export interface UseRoleplayRecordingResult {
  speakButtonRef: React.RefObject<HTMLButtonElement | null>;
}

export function useRoleplayRecording(
  params: UseRoleplayRecordingParams
): UseRoleplayRecordingResult {
  const { toggleRecording, status, openConfirmDialog, showMicDialog } = params;
  const speakButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let isMounted = true;

    const handleKeyDown = async (event: KeyboardEvent) => {
      if (status === 'processing' || status === 'waiting' || openConfirmDialog || showMicDialog) {
        return;
      }
      if (event.code === 'Space') {
        event.preventDefault();
        try {
          if (isMounted) {
            await toggleRecording();
          }
        } catch (error) {
          console.error('スペースキーでの録音切り替えに失敗しました:', error);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    if (speakButtonRef.current) {
      speakButtonRef.current.focus();
    }
    return () => {
      isMounted = false;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [status, openConfirmDialog, showMicDialog, toggleRecording]);

  return { speakButtonRef };
}
